<template>
  <b-modal
    v-model="showModal"
    title="RG Stock Report Details"
    size="lg"
    no-close-on-backdrop
    @ok="okHandler"
    @close="$emit('modal-closed')"
    @hidden="$emit('modal-closed')"
  >
    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>
    <b-alert
      variant="danger"
      :show="initialError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ initialError }}
        </p>
      </div>
    </b-alert>
    <b-form v-if="report && !loading && !initialError">
      <b-table-simple
        responsive
        hover
        class="compact-table mb-0"
      >
        <b-tbody>
          <b-tr>
            <b-th style="width: 20%;">
              Rack Group
            </b-th>
            <b-td style="width: 30%;">
              {{ report.rack_group_no }}
            </b-td>
            <b-th style="width: 20%;">
              Status
            </b-th>
            <b-td style="width: 30%;">
              {{ report.status }}
            </b-td>
          </b-tr>

          <b-tr>
            <b-th>
              Items Count
            </b-th>
            <b-td>
              {{ report.items_count }}
            </b-td>
            <b-th />
            <b-td />
          </b-tr>

          <b-tr>
            <b-th> Initiated At </b-th>
            <b-td> {{ report.check_initiated_at && formatedDate(report.check_initiated_at,'DD-MM-YYYY LT') || '-' }} </b-td>
            <b-th> Initiated By </b-th>
            <b-td> {{ report.check_initiated_by__name }} </b-td>
          </b-tr>
          <b-tr>
            <b-th> Completed At </b-th>
            <b-td> {{ report.check_completed_at && formatedDate(report.check_completed_at,'DD-MM-YYYY LT') || '-' }} </b-td>
            <b-th> Completed By </b-th>
            <b-td> {{ report.check_completed_by__name }} </b-td>
          </b-tr>
          <b-tr>
            <b-th>
              Report File
            </b-th>
            <b-td>
              <a
                v-if="report.report_file"
                :href="report.report_file"
                target="_blank"
              >
                <span>View File</span>
              </a>
              <span v-else>-</span>
            </b-td>
            <b-th>
              Verified File
            </b-th>
            <b-td>
              <span v-if="formData.verified_file">
                <a
                  :href="formData.verified_file"
                  target="_blank"
                >
                  <span>View File</span>
                </a>
                <b-button
                  class="btn-icon px-0 py-0"
                  variant="link"
                  @click="() => {
                    formData.verified_file = null
                  }"
                >
                  <feather-icon
                    color="red"
                    size="15"
                    icon="Trash2Icon"
                    class="ml-1"
                  />
                </b-button>
              </span>
              <span v-else>
                <b-form-file
                  v-model="formData.verified_file"
                  :disabled="loading || submitting || report.status === 'Created'"
                  accept=".pdf"
                />
              </span>
              <p
                v-if="errorMessage && errorMessage.verified_file"
                class="text-danger"
              >
                {{ errorMessage.verified_file }}
              </p>
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>

      <b-form-group label="Remarks">
        <b-form-textarea
          v-model="formData.remarks"
          placeholder="Remarks"
          :disabled="loading || submitting"
        />
      </b-form-group>
    </b-form>

    <template #modal-footer="{cancel}">
      <div class="d-flex">
        <b-button
          :disabled="loading || submitting"
          @click="cancel"
        >
          Close
        </b-button>
        <b-button
          :disabled="loading || submitting"
          class="ml-1"
          variant="primary"
          @click="okHandler"
        >
          Save
          <b-spinner
            v-if="submitting"
            small
            label="Small Spinner"
          />
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import axios from 'axios'
import {
  BModal, BFormTextarea, BForm, BButton, BSpinner, BTableSimple, BFormFile, BFormGroup,
  BTbody, BTd, BTr, BTh, BAlert,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import dateMixin from '@/mixins/dateMixin'

export default {
  components: {
    BModal,
    BFormTextarea,
    BForm,
    BButton,
    BSpinner,
    BTableSimple,
    BTbody,
    BTd,
    BTr,
    BTh,
    BFormFile,
    BAlert,
    BFormGroup,
  },
  mixins: [dateMixin],
  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      showModal: true,
      report: null,
      loading: false,
      initialError: null,
      submitting: false,
      errorMessage: null,
      formData: null,
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true
        this.initialError = null
        const { data } = await axios.get(`/stocks/rg_stock_reports/${this.$props.id}/`)
        this.report = data
        const { remarks, verified_file } = this.report
        this.formData = {
          remarks, verified_file,
        }
      } catch (error) {
        this.initialError = error?.response?.data?.detail || 'Failed to fetch details!'
      } finally {
        this.loading = false
      }
    },
    async okHandler(event) {
      try {
        event.preventDefault()

        this.errorMessage = null
        this.submitting = true

        const payload = new FormData()
        const { remarks, verified_file: verifiedFile } = this.formData
        payload.set('remarks', remarks)

        if (verifiedFile && typeof verifiedFile !== 'string') {
          payload.set('verified_file', verifiedFile)
        } else if (this.report.verified_file && !verifiedFile) {
          payload.set('delete_verified_file', true)
        }
        await axios.patch(`/stocks/rg_stock_reports/${this.$props.id}/`, payload)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Data updated successfully!',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.$emit('modal-closed')
      } catch (error) {
        this.errorMessage = error?.response?.data || 'Something Went Wrong!'
      } finally {
        this.submitting = false
        this.saveAndCloseLoader = false
      }
    },
  },
}
</script>
